import React, { useState, useEffect } from 'react';
import { StyleSheet, View, Text, ScrollView, StatusBar, RefreshControl } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ResidentMobileHeaderWithMenu } from '@/components/residents/mobile/ResidentMobileHeaderWithMenu';
import { GradientBorderFrame } from '@/components/common/admin/mobile/GradientBorderFrame';
import { useResidentScreen } from '@/hooks/residents/useResidentScreen';
import { PaymentSummaryProvider } from '@/features/payments/context/PaymentSummaryContext';
import { MobilePaymentCardV2 } from '@/features/payments/components/mobile/MobilePaymentCardV2';
import { MobilePaymentCardSkeleton } from '@/features/payments/components/mobile/MobilePaymentCardSkeleton';
import { MobileTransactionHistoryTable } from '@/features/payments/components/mobile/MobileTransactionHistoryTable';
import { MobileTransactionDetailsModal } from '@/features/payments/components/mobile/MobileTransactionDetailsModal';
import { useAppSelector, useAppDispatch } from '@/store';
import { selectLeaseIds, selectConnectionLeaseIds, linkedAcctsConnections, linkedAcctsConnectionsLoading } from '@/store/selectors';
import { useGetLeaseQuery, useGetLeaseEntrataDisabledQuery, useGetLeaseBalanceQuery } from '@/store/api';
import { useEntrataIntegrationDisabled } from '@/features/payments/hooks/useEntrataIntegrationDisabled';
import { useLeaseHistory } from '@/features/payments/hooks/useLeaseHistory';
import type { HistoryRow } from '@/features/payments/hooks/useLeaseHistory';
import { fetchLinkedAccounts } from '@/store/slices/residents/homeSlice';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { PAYMENT_DESIGN_TOKENS, PAYMENT_TYPOGRAPHY, PAYMENT_SPACING, PAYMENT_BORDER_RADIUS } from '@/constants/residents/mobile/paymentDesignTokens';
import { mobilePaymentsStrings } from '@/constants/residents/strings';
import { SVG_COMPONENTS } from '@/assets/admin';
import type { RecurringCharge } from '@/features/payments/components/mobile/RecurringChargesModal';

const GroupIcon = SVG_COMPONENTS.GROUP;

// Wrapper component to fetch and display individual lease data
interface LeasePaymentCardProps {
  leaseId: number;
  isConnection: boolean;
  showSeeDetails?: boolean;
}

const LeasePaymentCard: React.FC<LeasePaymentCardProps> = ({ leaseId, isConnection, showSeeDetails = false }) => {
  const entrataDisabled = useEntrataIntegrationDisabled();
  const authUser = useAppSelector((s) => (s as any).auth?.user);

  const { data: balanceData } = useGetLeaseBalanceQuery(leaseId, { skip: !leaseId });

  const leaseQuery = useGetLeaseQuery(
    { leaseId, includeTransactions: false, includePayments: true },
    { skip: !leaseId || entrataDisabled === true }
  );

  const leaseEntrataDisabledQuery = useGetLeaseEntrataDisabledQuery(
    { leaseId },
    { skip: !leaseId || entrataDisabled === false }
  );

  const isFetching = entrataDisabled ? leaseEntrataDisabledQuery.isFetching : leaseQuery.isFetching;
  const isError = entrataDisabled ? leaseEntrataDisabledQuery.isError : leaseQuery.isError;
  const isSuccess = entrataDisabled ? leaseEntrataDisabledQuery.isSuccess : leaseQuery.isSuccess;
  const data = entrataDisabled ? leaseEntrataDisabledQuery.data : leaseQuery.data;

  if (isFetching) {
    return <MobilePaymentCardSkeleton />;
  }

  if (isError || !isSuccess || !data) {
    return null;
  }

  const response = (data as any)?.response || data;
  const lease = response?.lease || response?.lease?.id ? response.lease : response;
  const property = lease?.property || {};
  const users = lease?.users || [];
  const scheduledCharges = lease?.scheduledCharges || [];
  const paymentsBlocked = !!lease?.paymentsBlocked;
  const recurringPayments = lease?.recurringPayments || [];
  const autopayEnabled = Array.isArray(recurringPayments) && recurringPayments.length > 0;
  const balanceDue = Number(balanceData?.balanceDue ?? 0);
  const balanceItems = balanceData?.balanceItems;
  const unitNumberSpace = lease?.unitNumberSpace || lease?.unitNumber || '';
  
  // Count pending payments from the response
  const payments: any[] = response?.payments || [];
  const pendingPaymentsCount = payments.filter(
    (p: any) => !p.settlementStatus || p.settlementStatus === 'Pending Finalization'
  ).length;

  // Determine resident info for display
  const connectedPrimary = users?.find((u: any) => u?.id !== authUser?.id && u?.LeaseCustomer?.customerType === 'primary');
  const displayUser = isConnection 
    ? (connectedPrimary || users?.find((u: any) => u?.id !== authUser?.id) || users?.[0] || {})
    : (users?.find((u: any) => u?.id === authUser?.id) || users?.[0] || {});

  const residentName = displayUser?.firstName && displayUser?.lastName
    ? `${displayUser.firstName} ${displayUser.lastName}`
    : 'Resident';

  // Check if current user is primary leaseholder
  const currentUserLease = users?.find((u: any) => u?.id === authUser?.id);
  const isPrimaryLeaseholder = currentUserLease?.LeaseCustomer?.customerType === 'primary';

  // Convert scheduled charges to RecurringCharge format
  const recurringCharges: RecurringCharge[] = scheduledCharges.map((charge: any) => ({
    name: charge.chargeCode || charge.description || 'Charge',
    amount: charge.amount ? Number(charge.amount) : null,
    frequency: 'Monthly',
  }));

  const handleAutopayUpdated = () => {
    // Refetch lease data when autopay is updated
    if (entrataDisabled) {
      leaseEntrataDisabledQuery.refetch();
    } else {
      leaseQuery.refetch();
    }
  };

  return (
    <MobilePaymentCardV2
      leaseId={leaseId}
      residentName={residentName}
      propertyName={property?.marketingName || ''}
      unitNumber={unitNumberSpace}
      balanceDue={balanceDue}
      balanceItems={balanceItems}
      autopayEnabled={autopayEnabled}
      isConnection={isConnection}
      paymentsBlocked={paymentsBlocked}
      isPrimaryLeaseholder={isPrimaryLeaseholder}
      scheduledCharges={recurringCharges}
      showSeeDetails={showSeeDetails}
      firstName={displayUser?.firstName}
      lastName={displayUser?.lastName}
      photoUrl={displayUser?.photoUrl}
      onAutopayUpdated={handleAutopayUpdated}
      pendingPaymentsCount={pendingPaymentsCount}
    />
  );
};

const PaymentsContent: React.FC = () => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const insets = useSafeAreaInsets();
  
  const ownLeaseIds = useAppSelector(selectLeaseIds);
  const connectionLeaseIds = useAppSelector(selectConnectionLeaseIds);
  const linkedAccounts = useAppSelector(linkedAcctsConnections);
  const isLoading = useAppSelector(linkedAcctsConnectionsLoading);
  const [refreshing, setRefreshing] = useState(false);
  const authUser = useAppSelector((s) => (s as any).auth?.user);
  const userId = authUser?.id;

  // Use resident screen hook for navigation handling
  const {
    handleNavigate,
    handleLogoPress,
    handleSignOut,
    handleNotificationPress,
  } = useResidentScreen('ResidentPayments', navigation);

  // Fetch linked accounts on mount
  useEffect(() => {
    if (!linkedAccounts || linkedAccounts.length === 0) {
      dispatch(fetchLinkedAccounts());
    }
  }, [dispatch]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await dispatch(fetchLinkedAccounts()).unwrap();
    } catch (error) {
      console.error('Failed to refresh:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const allLeaseIds = [...ownLeaseIds, ...connectionLeaseIds];
  const hasLeases = allLeaseIds.length > 0;
  const hasMultipleLeases = allLeaseIds.length > 1;

  // Get transaction history for first lease (or all leases combined)
  const primaryLeaseId = ownLeaseIds[0] || connectionLeaseIds[0];
  const { 
    rows: transactions, 
    isFetching: transactionsLoading,
    hasError: transactionsError,
  } = useLeaseHistory(primaryLeaseId, userId);

  // State for transaction details modal
  const [selectedTransaction, setSelectedTransaction] = useState<HistoryRow | null>(null);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);

  const handleTransactionPress = (transaction: HistoryRow) => {
    setSelectedTransaction(transaction);
    setShowTransactionDetails(true);
  };

  const handleCloseTransactionDetails = () => {
    setShowTransactionDetails(false);
    setSelectedTransaction(null);
  };

  const renderEmptyState = () => (
    <View style={styles.emptyStateContainer}>
      {/* Empty State Card - No Recipients */}
      <View style={styles.emptyStateCard}>
        {/* Group Icon */}
        <View style={styles.emptyIconContainer}>
          {GroupIcon && (
            <GroupIcon width={48} height={48} color={PAYMENT_DESIGN_TOKENS.TEXT_TERTIARY} />
          )}
        </View>

        {/* Title */}
        <Text style={styles.emptyTitle}>No recepients</Text>

        {/* Description */}
        <Text style={styles.emptyDescription}>
          Once you receive and accept an invitation your recepients will be displayed here
        </Text>
      </View>

      {/* Empty Transaction History */}
      <View style={styles.emptyHistorySection}>
        <Text style={styles.historyTitle}>{mobilePaymentsStrings.transactionHistory}</Text>
        <Text style={styles.emptyHistoryText}>
          {mobilePaymentsStrings.emptyTransactionHistory}
        </Text>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" />
      
      {/* Header with Navigation Menu */}
      <ResidentMobileHeaderWithMenu
        onLogoPress={handleLogoPress}
        onSignOut={handleSignOut}
        onNavigate={handleNavigate}
        onNotificationPress={handleNotificationPress}
        currentPath="/payments"
        currentTitle="Payments"
        showNotification={true}
      />

      {/* Outer gradient frame - 3px border, 32px radius */}
      <GradientBorderFrame
        borderWidth={3}
        borderRadius={32}
        innerStyle={styles.outerFrameInner}
      >
        <View style={styles.innerFrameSpacing}>
          {/* Inner gradient frame - 1px border, 27px radius */}
          <GradientBorderFrame
            borderWidth={1}
            borderRadius={27}
            innerStyle={styles.innerFrameContent}
          >
      {/* Content */}
      {isLoading && !hasLeases ? (
        <View style={styles.loadingContainer}>
          <LoadingSpinner message="Loading payments..." variant="admin" />
        </View>
      ) : !hasLeases ? (
        <ScrollView 
          style={styles.scrollContent}
          contentContainerStyle={{ paddingBottom: insets.bottom }}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              tintColor={PAYMENT_DESIGN_TOKENS.PINK_ACCENT}
            />
          }
        >
          {renderEmptyState()}
        </ScrollView>
      ) : (
        <ScrollView
          style={styles.scrollContent}
          contentContainerStyle={{ paddingBottom: insets.bottom }}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              tintColor={PAYMENT_DESIGN_TOKENS.PINK_ACCENT}
            />
          }
        >
                {/* Lease Cards */}
                <View style={styles.cardsContainer}>
                  {/* Own Leases Section */}
                  {ownLeaseIds.length > 0 && hasMultipleLeases && (
                    <Text style={styles.sectionHeader}>
                      You have {ownLeaseIds.length} {ownLeaseIds.length === 1 ? 'lease' : 'leases'}
            </Text>
          )}
            {ownLeaseIds.map((leaseId) => (
              <LeasePaymentCard 
                key={`own-${leaseId}`}
                leaseId={leaseId}
                isConnection={false}
                showSeeDetails={hasMultipleLeases}
              />
            ))}
                  
                  {/* Connection Leases Section */}
                  {connectionLeaseIds.length > 0 && hasMultipleLeases && (
                    <Text style={styles.sectionHeader}>
                      Your connections have {connectionLeaseIds.length} {connectionLeaseIds.length === 1 ? 'lease' : 'leases'}
                    </Text>
                  )}
            {connectionLeaseIds.map((leaseId) => (
              <LeasePaymentCard
                key={`conn-${leaseId}`}
                leaseId={leaseId}
                isConnection={true}
                showSeeDetails={hasMultipleLeases}
              />
            ))}
          </View>

          {/* Transaction History Table - only show for single lease or first lease */}
          {!hasMultipleLeases && primaryLeaseId && (
            <View style={styles.historyContainer}>
              <MobileTransactionHistoryTable
                transactions={transactions}
                isLoading={transactionsLoading}
                pageSize={10}
                onTransactionPress={handleTransactionPress}
              />
            </View>
          )}
        </ScrollView>
      )}
          </GradientBorderFrame>
        </View>
      </GradientBorderFrame>

      {/* Transaction Details Modal */}
      <MobileTransactionDetailsModal
        visible={showTransactionDetails}
        onClose={handleCloseTransactionDetails}
        transaction={selectedTransaction}
      />
    </View>
  );
};

export const ResidentPaymentsScreen: React.FC = () => {
  return (
    <PaymentSummaryProvider>
      <PaymentsContent />
    </PaymentSummaryProvider>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: PAYMENT_DESIGN_TOKENS.CARD_BACKGROUND_SECONDARY, // #242424 - matches nav header
  },
  outerFrameInner: {
    backgroundColor: PAYMENT_DESIGN_TOKENS.BACKGROUND, // #171717 - darker inside gradient frame
  },
  innerFrameSpacing: {
    flex: 1,
    paddingTop: 2,
    paddingHorizontal: 2,
  },
  innerFrameContent: {
    flex: 1,
    backgroundColor: PAYMENT_DESIGN_TOKENS.BACKGROUND,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollContent: {
    flex: 1,
  },
  sectionHeader: {
    ...PAYMENT_TYPOGRAPHY.headingLg,
    color: PAYMENT_DESIGN_TOKENS.TEXT_PRIMARY,
    marginBottom: PAYMENT_SPACING.lg,
    marginTop: PAYMENT_SPACING.md,
  },
  cardsContainer: {
    paddingHorizontal: PAYMENT_SPACING.lg,
    paddingTop: PAYMENT_SPACING.lg,
  },
  historyContainer: {
    paddingHorizontal: PAYMENT_SPACING.xl,
    paddingBottom: PAYMENT_SPACING.xxl,
  },
  // Empty State Styles
  emptyStateContainer: {
    flex: 1,
    paddingHorizontal: PAYMENT_SPACING.lg,
    paddingTop: PAYMENT_SPACING.lg,
  },
  emptyStateCard: {
    backgroundColor: PAYMENT_DESIGN_TOKENS.CARD_BACKGROUND,
    borderRadius: PAYMENT_BORDER_RADIUS.xl,
    paddingVertical: PAYMENT_SPACING.xxxl,
    paddingHorizontal: PAYMENT_SPACING.xl,
    alignItems: 'center',
  },
  emptyIconContainer: {
    marginBottom: PAYMENT_SPACING.lg,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: PAYMENT_DESIGN_TOKENS.TEXT_PRIMARY,
    marginBottom: PAYMENT_SPACING.sm,
    textAlign: 'center',
  },
  emptyDescription: {
    ...PAYMENT_TYPOGRAPHY.bodySm,
    color: PAYMENT_DESIGN_TOKENS.TEXT_MUTED,
    textAlign: 'center',
    lineHeight: 21,
  },
  emptyHistorySection: {
    marginTop: PAYMENT_SPACING.xxl,
    paddingHorizontal: PAYMENT_SPACING.xs,
  },
  historyTitle: {
    ...PAYMENT_TYPOGRAPHY.headingLg,
    color: PAYMENT_DESIGN_TOKENS.TEXT_PRIMARY,
    marginBottom: PAYMENT_SPACING.lg,
  },
  emptyHistoryText: {
    ...PAYMENT_TYPOGRAPHY.bodySm,
    color: PAYMENT_DESIGN_TOKENS.TEXT_MUTED,
    lineHeight: 21,
  },
});
